import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import { compose } from 'redux';

import { Button, Checkbox } from '@pearsontechnology/aero-components';

import { featureKeys } from 'services/FeatureFlagService';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import { ModalActions } from 'components/Modal';
import { publishFormat } from '../../utils/constants';
import courseTypes from 'constants/courseTypes';
import messages from './messages';
import {
  AddModalWrapper,
  OutputTypeItem,
  ModalTitle,
} from './styles';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';

const BASE_OUTPUT_OPTIONS = [
  { type: publishFormat.EPUB, label: messages.EPUB_ETEXT },
  { type: publishFormat.PEARSON_PLUS, label: messages.BRONTE_PEARSON_PLUS },
  { type: publishFormat.CHANNEL_PARTNER, label: messages.EPUB_CHANNEL_PARTNER },
  { type: publishFormat.IN_DESIGN, label: messages.IN_DESIGN },
  { type: publishFormat.CLASS_ON_DEMAND, label: messages.BRONTE_CLASSES_ON_DEMAND },
  { type: publishFormat.PEARSON_PLUS_TEST, label: messages.BRONTE_PEARSON_PLUS_TEST_ONLY },
];

const ALLOWED_BY_COURSEWARE = {
  [courseTypes.ilab]: [publishFormat.CLASS_ON_DEMAND],
  [courseTypes.ePub]: [
    publishFormat.EPUB,
    publishFormat.CHANNEL_PARTNER,
    publishFormat.IN_DESIGN,
  ],
  [courseTypes.bronteViewer]: [
    publishFormat.PEARSON_PLUS,
    publishFormat.PEARSON_PLUS_TEST,
    publishFormat.CHANNEL_PARTNER,
    publishFormat.IN_DESIGN,
  ],
};

function computeFormatLabels(
  coursewarePluginType,
  filterOutputOptionsEnabled,
  isChannelPartnerOutputTypeEnabled,
  isPearsonPlusOutputTypeEnabled,
  isClassOnDemandOutputTypeEnabled,
  isPearsonPlusTestOutputTypeEnabled,
  isIndesignOutputTypeEnabled
) {
  let options = [...BASE_OUTPUT_OPTIONS];
  const hasCoursewareType =
    coursewarePluginType != null && coursewarePluginType !== '';

  if (filterOutputOptionsEnabled && hasCoursewareType) {
    const allowed = new Set(ALLOWED_BY_COURSEWARE[coursewarePluginType] ?? []);
    options = options.filter((item) => allowed.has(item.type));
  }

  if (!isChannelPartnerOutputTypeEnabled) {
    options = options.filter((item) => item.type !== publishFormat.CHANNEL_PARTNER);
  }
  if (!isClassOnDemandOutputTypeEnabled) {
    options = options.filter((item) => item.type !== publishFormat.CLASS_ON_DEMAND);
  }
  if (!isPearsonPlusOutputTypeEnabled) {
    options = options.filter((item) => item.type !== publishFormat.PEARSON_PLUS);
  }
  if (!isPearsonPlusTestOutputTypeEnabled) {
    options = options.filter((item) => item.type !== publishFormat.PEARSON_PLUS_TEST);
  }
  if (!isIndesignOutputTypeEnabled) {
    options = options.filter((item) => item.type !== publishFormat.IN_DESIGN);
  }

  return options;
}

function PublishOutputType({ onDismiss, setPublicationOutputType, getFeatureFlagValue, coursewarePluginType }) {
  const isChannelPartnerOutputTypeEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_PUBICATION_OUTPUT_TYPE_CHANNEL_PARTNER);
  const isPearsonPlusOutputTypeEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_PUBLICATIONS_PEARSON_PLUS_ETEXT);
  const isClassOnDemandOutputTypeEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_PUBLICATIONS_CLASSONDEMAND);
  const isPearsonPlusTestOutputTypeEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_PUBLICATIONS_BV_TEST_ONLY);
  const isIndesignOutputTypeEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_PUBLICATIONS_INDESIGN);
  const filterOutputOptionsEnabled = getFeatureFlagValue(featureKeys.DUPLICATE_MATCH_SOURCE_PLUGIN);
  const formatLabels = useMemo(
    () =>
      computeFormatLabels(
        coursewarePluginType,
        filterOutputOptionsEnabled,
        isChannelPartnerOutputTypeEnabled,
        isPearsonPlusOutputTypeEnabled,
        isClassOnDemandOutputTypeEnabled,
        isPearsonPlusTestOutputTypeEnabled,
        isIndesignOutputTypeEnabled
      ),
    [
      coursewarePluginType,
      filterOutputOptionsEnabled,
      isChannelPartnerOutputTypeEnabled,
      isPearsonPlusOutputTypeEnabled,
      isClassOnDemandOutputTypeEnabled,
      isPearsonPlusTestOutputTypeEnabled,
      isIndesignOutputTypeEnabled,
    ]
  );
  const [formatSelected, setFormatSelected] = useState(publishFormat.EPUB);

  useEffect(() => {
    setFormatSelected((prev) =>
      formatLabels.some((item) => item.type === prev)
        ? prev
        : (formatLabels[0]?.type ?? publishFormat.EPUB)
    );
  }, [formatLabels]);

  const onNext = () => {
    setPublicationOutputType(formatSelected);
  };

  const selectNewOutputType = (outputFormatItem) => {
    setFormatSelected(outputFormatItem.type);
  };

  const checkBoxes = formatLabels.map((item, i) =>
    <OutputTypeItem key={i}>
      <Checkbox label={intl.formatMessage(item.label)}
        rounded
        checked={formatSelected === item.type}
        onChange={() => selectNewOutputType(item)}
      />
    </OutputTypeItem>);

  return (
    <AddModalWrapper>
      <ModalTitle>{intl.formatMessage(messages.outputSelectTitle)}</ModalTitle>
      {checkBoxes}
      <ModalActions>
        <Button grey onClick={onDismiss}>{intl.formatMessage(messages.cancelButton)}</Button>
        <Button primary onClick={onNext}>{intl.formatMessage(messages.nextButton)}</Button>
      </ModalActions>
    </AddModalWrapper>
  );
}

PublishOutputType.propTypes = {
  onDismiss: PropTypes.func,
  onAddPublicationOutputType: PropTypes.func,
  coursewarePluginType: PropTypes.string,
};

const flagNames = [
  featureKeys.WORKSPACE_PUBICATION_OUTPUT_TYPE_CHANNEL_PARTNER,
  featureKeys.WORKSPACE_PUBLICATIONS_PEARSON_PLUS_ETEXT,
  featureKeys.WORKSPACE_PUBLICATIONS_CLASSONDEMAND,
  featureKeys.WORKSPACE_PUBLICATIONS_BV_TEST_ONLY,
  featureKeys.WORKSPACE_PUBLICATIONS_INDESIGN,
  featureKeys.DUPLICATE_MATCH_SOURCE_PLUGIN,
];

export default compose(withTheme, enhanceWithFeatureFlagSubscription)(PublishOutputType, flagNames);
